import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Badge, Spinner } from 'react-bootstrap';
import { keysAPI } from '../../services/api';
import useProductsStore from '../../store/productsStore';
import { toast } from 'react-toastify';
import AdminLayout from '../../components/AdminLayout';

const Keys = () => {
  const { products, fetchProducts } = useProductsStore();
  const [selectedProduct, setSelectedProduct] = useState('');
  const [keyStats, setKeyStats] = useState(null);
  const [keys, setKeys] = useState([]);
  const [manualKeys, setManualKeys] = useState('');
  const [csvFile, setCsvFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchProducts({ limit: 100 });
  }, [fetchProducts]);

  useEffect(() => {
    if (selectedProduct) {
      fetchKeyStats();
      fetchProductKeys();
    }
  }, [selectedProduct]);

  const fetchKeyStats = async () => {
    try {
      const response = await keysAPI.getAvailable(selectedProduct);
      setKeyStats(response.data);
    } catch (error) {
      console.error('Failed to fetch key stats');
    }
  };

  const fetchProductKeys = async () => {
    try {
      const response = await keysAPI.getProductKeys(selectedProduct, { limit: 50 });
      setKeys(response.data.keys);
    } catch (error) {
      console.error('Failed to fetch keys');
    }
  };

  const handleManualUpload = async (e) => {
    e.preventDefault();
    if (!selectedProduct) {
      toast.error('Please select a product');
      return;
    }

    const keysArray = manualKeys.split('\n').filter(k => k.trim() !== '');
    if (keysArray.length === 0) {
      toast.error('Please enter at least one key');
      return;
    }

    setIsLoading(true);
    try {
      await keysAPI.upload({ productId: selectedProduct, keys: keysArray });
      toast.success('Keys uploaded successfully');
      setManualKeys('');
      fetchKeyStats();
      fetchProductKeys();
    } catch (error) {
      toast.error('Failed to upload keys');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCSVUpload = async (e) => {
    e.preventDefault();
    if (!selectedProduct) {
      toast.error('Please select a product');
      return;
    }

    if (!csvFile) {
      toast.error('Please select a CSV file');
      return;
    }

    const formData = new FormData();
    formData.append('file', csvFile);
    formData.append('productId', selectedProduct);

    setIsLoading(true);
    try {
      await keysAPI.uploadCSV(formData);
      toast.success('Keys uploaded from CSV successfully');
      setCsvFile(null);
      fetchKeyStats();
      fetchProductKeys();
    } catch (error) {
      toast.error('Failed to upload CSV');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteKey = async (keyId) => {
    if (window.confirm('Are you sure you want to delete this key?')) {
      try {
        await keysAPI.delete(keyId);
        toast.success('Key deleted successfully');
        fetchKeyStats();
        fetchProductKeys();
      } catch (error) {
        toast.error('Failed to delete key');
      }
    }
  };

  return (
    <AdminLayout>
    <Container fluid className="my-4">
      <h1 className="mb-4">License Key Management</h1>

      <Row className="mb-4">
        <Col md={12}>
          <Card>
            <Card.Body>
              <Form.Group>
                <Form.Label>Select Product</Form.Label>
                <Form.Select
                  value={selectedProduct}
                  onChange={(e) => setSelectedProduct(e.target.value)}
                >
                  <option value="">Choose a product...</option>
                  {products.map((product) => (
                    <option key={product._id} value={product._id}>
                      {product.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              {keyStats && (
                <div className="mt-3">
                  <Badge bg="success" className="me-2">Available: {keyStats.available}</Badge>
                  <Badge bg="danger" className="me-2">Used: {keyStats.used}</Badge>
                  <Badge bg="info">Total: {keyStats.total}</Badge>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {selectedProduct && (
        <>
          <Row className="mb-4">
            <Col md={6}>
              <Card>
                <Card.Header>
                  <h5>Manual Upload</h5>
                </Card.Header>
                <Card.Body>
                  <Form onSubmit={handleManualUpload}>
                    <Form.Group className="mb-3">
                      <Form.Label>Enter License Keys (one per line)</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={10}
                        value={manualKeys}
                        onChange={(e) => setManualKeys(e.target.value)}
                        placeholder="XXXXX-XXXXX-XXXXX&#10;YYYYY-YYYYY-YYYYY"
                      />
                    </Form.Group>
                    <Button type="submit" variant="primary" disabled={isLoading}>
                      {isLoading ? 'Uploading...' : 'Upload Keys'}
                    </Button>
                  </Form>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6}>
              <Card>
                <Card.Header>
                  <h5>CSV Upload</h5>
                </Card.Header>
                <Card.Body>
                  <Form onSubmit={handleCSVUpload}>
                    <Form.Group className="mb-3">
                      <Form.Label>Upload CSV File</Form.Label>
                      <Form.Control
                        type="file"
                        accept=".csv"
                        onChange={(e) => setCsvFile(e.target.files[0])}
                      />
                      <Form.Text className="text-muted">
                        CSV should have a column named 'key' or 'license_key'
                      </Form.Text>
                    </Form.Group>
                    <Button type="submit" variant="primary" disabled={isLoading}>
                      {isLoading ? 'Uploading...' : 'Upload CSV'}
                    </Button>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col>
              <Card>
                <Card.Header>
                  <h5>License Keys</h5>
                </Card.Header>
                <Card.Body>
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>Key</th>
                        <th>Status</th>
                        <th>Used By</th>
                        <th>Order ID</th>
                        <th>Used At</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {keys.map((key) => (
                        <tr key={key._id}>
                          <td><code>{key.key}</code></td>
                          <td>
                            <Badge bg={key.isUsed ? 'danger' : 'success'}>
                              {key.isUsed ? 'Used' : 'Available'}
                            </Badge>
                          </td>
                          <td>{key.usedBy?.name || 'N/A'}</td>
                          <td>
                            {key.orderId ? <small>{key.orderId.toString().slice(-8)}</small> : 'N/A'}
                          </td>
                          <td>{key.usedAt ? new Date(key.usedAt).toLocaleString() : 'N/A'}</td>
                          <td>
                            {!key.isUsed && (
                              <Button
                                size="sm"
                                variant="danger"
                                onClick={() => handleDeleteKey(key._id)}
                              >
                                Delete
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Container>
    </AdminLayout>
  );
};

export default Keys;
